import {
    useState,
    useCallback,
} from 'react';

import { InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from "@azure/msal-react";

/**
 * Custom hook to call a web API using bearer token obtained from MSAL
 * @param {PopupRequest} msalRequest 
 * @returns 
 */
const useFetchWithMsal = (msalRequest) => {
    const { instance } = useMsal();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const { result, error: msalError } = useMsalAuthentication(InteractionType.Popup, {
        ...msalRequest,
        account: instance.getActiveAccount(),
        redirectUri: '/redirect'
    });

    /**
     * Execute a fetch request with the given options
     * @param {string} method: GET, POST, PUT, DELETE
     * @param {String} endpoint: The endpoint to call
     * @param {Object} data: The data to send to the endpoint, if any 
     * @returns JSON response
     */
    const execute = async (method, endpoint, data = null, throwErrorOnNonSuccess = true) => {
        if (msalError) {
            setError(msalError);
            return;
        }

        if (result) {
            try {
                const headers = new Headers();
                const bearer = `Bearer ${result.accessToken}`;
                headers.append("Authorization", bearer);

                if (data) headers.append('Content-Type', 'application/json');

                let options = {
                    method: method,
                    headers: headers,
                    body: data ? JSON.stringify(data) : null,
                };

                setIsLoading(true);
                let response = (await fetch(endpoint, options));
                if ((response.status === 200 || response.status === 201)) {
                    let responseData = null;
                    const contentType = response.headers.get("content-type");

                    try {
                        if (contentType && contentType.indexOf("application/json") !== -1) {
                            let responseBody = await response.text();
                            if(responseBody && responseBody.length > 0) {
                                responseData = JSON.parse(responseBody);
                            } else if(responseBody){
                                responseData = responseBody;
                            }
                        } else{
                            responseData = await response.text();
                        }
                    } catch (error) {
                        console.log(error);
                        setIsLoading(false);
                        setError(error);
                    } finally {
                        setData(responseData);
                        setIsLoading(false);
                        return responseData;
                    }
                }
                else if (throwErrorOnNonSuccess) {
                    let errorResponse = null;
                    const contentType = response.headers.get("content-type");

                    try {
                        if (contentType && contentType.indexOf("application/json") !== -1) {
                            let responseBody = await response.text();
                            if (responseBody && responseBody.length > 0) {
                                errorResponse = JSON.parse(responseBody);
                            } else if (responseBody) {
                                errorResponse = responseBody;
                            }
                        } else {
                            errorResponse = await response.text();
                        }
                    } catch (parseError) {
                        console.error("Failed to parse error response:", parseError);
                    }
                
                    const errorMessage =
                        errorResponse?.message || 
                        errorResponse?.error || 
                        JSON.stringify(errorResponse) ||
                        "An unexpected error occurred.";
                
                    // Throw a detailed error
                    throw new Error(`Error ${response.status}: ${response.statusText} - ${errorMessage}`);
                }                
                setIsLoading(false);
                return response;
            } catch (e) {
                setError(e);
                setIsLoading(false);
                throw e;
            }
        }
    };

    return {
        isLoading,
        error,
        data,
        execute: useCallback(execute, [result, msalError]), // to avoid infinite calls when inside a `useEffect`
    };
};

export default useFetchWithMsal;